import { useAtom } from 'jotai';
import { accessibilityDataAtom, demoModeAtom } from '@/global-store';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarInset,
} from '@layout/Sidebar/Sidebar';
import { useSidebar } from '@layout/Sidebar/SidebarContext';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Outlet, useNavigation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { MessageCircleQuestion } from 'lucide-react';
import { useFetchAccessibilityData } from '@http/queries';
import MenuList from './Sidebar/MenuList/MenuList';
import Header from '@layout/Header/Header';
import { Button, useTailwindBreakpoint } from '@eye-able/ui';
import Footer from '@layout/Footer';
import LoadingSpinner from '@components/LoadingSpinner';
import { ReportAreaContent } from '@layout/ReportAreaContent';
import { usePathname } from '@hooks/usePathname';
import { cn } from '@helpers/cn';
import OfferContactForm from '@components/OfferContactForm';
import theme from '@assets/theme';
import { Box } from '@mui/material';

export function MainLayout() {
  const { isMobile, openMobile, state } = useSidebar();

  const [, setAccessibilityData] = useAtom(accessibilityDataAtom);
  const [demoMode] = useAtom(demoModeAtom);

  const { t } = useTranslation();

  const path = usePathname();

  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [path]);

  const isReportArea = path.startsWith('/report');
  const isRulesArea = path.startsWith('/rulesOverview');

  // fetch the accessibility data for the current auditEntry and update the atom if the data changes
  const { isError, isLoading } = useFetchAccessibilityData({
    onDataChanged: (data) => setAccessibilityData(data),
  });

  const xlAndUp = useTailwindBreakpoint('xl');

  const navigation = useNavigation();

  const isPageLoading =
    navigation.state === 'loading' ||
    ((isReportArea || isRulesArea) && isLoading);

  const renderMainContent = () => {
    if (isPageLoading) {
      return <LoadingSpinner />;
    }
    if (isReportArea) {
      return <ReportAreaContent isError={isError} />;
    }
    return <Outlet />;
  };

  const shouldShowDemoButtons = demoMode
    ? isMobile
      ? !openMobile
      : state === 'collapsed'
    : false;

  return (
    <div
      className='eye:flex eye:flex-col eye:h-svh eye:min-h-0 eye:w-full'
      style={{
        ['--app-header-height']: `${xlAndUp ? 75 : 52}px`,
        height: '100svh',
      }}
    >
      <Header />
      <div aria-hidden style={{ height: 'var(--app-header-height)' }} />
      <nav className='eye:bg-grey-10 eye:flex eye:w-full eye:flex-1 eye:min-h-0 eye:overflow-hidden'>
        <Sidebar
          className='no-print'
          collapsible='icon'
          side='left'
          variant='inset'
        >
          <SidebarContent>
            <MenuList />
          </SidebarContent>
          {!demoMode && (
            <SidebarFooter>
              <div id='support-popup'>
                <Button className='eye:w-full' variant='outline' asChild>
                  <RouterLink to='/help-center'>
                    <MessageCircleQuestion />
                    {t('helpCenter')}
                  </RouterLink>
                </Button>
              </div>
            </SidebarFooter>
          )}
        </Sidebar>

        <SidebarInset
          ref={scrollRef}
          className={cn(
            'eye:min-h-0 eye:min-w-0 eye:flex-1 eye:max-w-full',
            'eye:px-16 eye:pt-32 eye:sm:px-6 eye:sm:py-6 eye:lg:px-48 eye:lg:py-[40px]',
            'eye:flex eye:flex-col eye:flex-nowrap',
            'eye:overflow-x-hidden eye:overflow-y-auto eye:wrap-break-word',
          )}
        >
          <div className='eye:w-full eye:flex-1 eye:max-w-[1500px] eye:mx-auto'>
            {renderMainContent()}
          </div>
          <div className='eye:w-full eye:max-w-[1500px] eye:mx-auto eye:mt-auto eye:pt-xs eye:pb-4xs'>
            <Footer />
          </div>
        </SidebarInset>
      </nav>

      {shouldShowDemoButtons ? (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            zIndex: 1150,
            width: '100%',

            '& .MuiButton-root': {
              width: '100%',
              borderRadius: 0,
            },

            '@media (min-width:1190px)': {
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'auto',

              '& .MuiButton-root': {
                width: 'auto !important',
                borderRadius: '50px !important',
              },

              top: '16.5px',
              bottom: 'unset',
            },

            '@media (max-width:1189px)': {
              '& .MuiButton-root, & #offer-contact-button': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.black.main,
                width: '50%',
              },
              '& .MuiButton-root': {
                borderRight: '0.75px solid #000',
              },
              '& #offer-contact-button .MuiButton-root': {
                width: '100%',
                borderLeft: '0.75px solid #000',
                borderRight: 'none',
              },
            },

            '@media (max-width:579px)': {
              '& .MuiButton-root, & #offer-contact-button': {
                width: '100%',
              },
            },
          }}
        >
          <OfferContactForm
            buttonColor='secondary'
            fullWidthMobile
            size='small'
            showBookConsultation
          />
        </Box>
      ) : null}
    </div>
  );
}
